import { useEffect, useState, useCallback } from "react";
import { Loader2, BrainCircuit, Globe, Compass, BarChart3, ScrollText, Plus, Trash2, CheckCircle2, XCircle, Ban, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, KpiCard, Panel, useAdmin } from "@/components/admin/ui";

const TABS = [
  { id: "overview", label: "Overview", icon: BrainCircuit },
  { id: "domains", label: "Trusted Domains", icon: Globe },
  { id: "discovered", label: "Discovered Sources", icon: Compass },
  { id: "usage", label: "Usage & Cost", icon: BarChart3 },
  { id: "audit", label: "Audit Log", icon: ScrollText },
];
const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#1F2A22]";

function Overview() {
  const [d, setD] = useState(null);
  const [health, setHealth] = useState(null);
  useEffect(() => { api.aiHubOverview().then(setD).catch(() => setD(null)); }, []);
  const runHealth = async () => {
    setHealth("loading");
    try { setHealth(await api.aiHubHealth()); } catch { setHealth({ ok: false, checks: {}, hint: "Health check request failed." }); }
  };
  if (!d) return <Loader2 className="animate-spin text-[#FF6A00]" />;
  return (
    <div className="space-y-6" data-testid="aihub-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Sources ready" value={`${d.sources.ready}/${d.sources.total}`} />
        <KpiCard label="Searchable sections" value={d.sources.sections} />
        <KpiCard label="States with sources" value={d.sources.states_with_sources} />
        <KpiCard label="Failed sources" value={d.sources.failed} />
        <KpiCard label="AI requests (7d)" value={d.activity_7d.requests} />
        <KpiCard label="Web research (7d)" value={`${d.activity_7d.web_pct}%`} />
        <KpiCard label="Conversations" value={d.conversations} />
        <KpiCard label="Discovered (new)" value={d.discovered_new} />
      </div>
      <Panel title="Environment health">
        <p className="text-[12px] text-[#5C6B61] mb-3">Checks the live services the AI needs. Run this in the environment where indexing is failing to pinpoint the cause.</p>
        <button onClick={runHealth} className="inline-flex items-center gap-1.5 bg-[#1F2A22] text-white px-3.5 py-2 text-sm mb-3" data-testid="aihub-health-run">
          {health === "loading" ? <Loader2 size={15} className="animate-spin" /> : <BrainCircuit size={15} />} Run health check
        </button>
        {health && health !== "loading" && (
          <div className="space-y-1.5" data-testid="aihub-health-result">
            {Object.entries(health.checks).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                {v.ok ? <CheckCircle2 size={15} className="text-[#2E7D46]" /> : <XCircle size={15} className="text-[#B04A3A]" />}
                <span className="font-medium text-[#1F2A22] capitalize">{k.replace(/_/g, " ")}</span>
                <span className="text-[#8A8478] text-[12px]">{v.detail || v.error}</span>
              </div>
            ))}
            {health.hint && <p className="text-[12px] text-[#B04A3A] mt-2">{health.hint}</p>}
          </div>
        )}
      </Panel>
      <Panel title="How HomeRange AI answers">
        <ol className="text-sm text-[#5C6B61] space-y-1.5 list-decimal ml-5">
          <li>Stored official state documents & verified draw data (fastest, authoritative).</li>
          <li>Google Search grounding on official sources — only when stored knowledge is thin.</li>
          <li>Gemini synthesizes a natural, cited answer; official sources are prioritized.</li>
        </ol>
        <p className="text-[11px] text-[#8A8478] mt-3">{d.trusted_domains} trusted official domains configured · needs_review: {d.sources.needs_review}</p>
      </Panel>
    </div>
  );
}

function TrustedDomains({ canEdit }) {
  const [rows, setRows] = useState(null);
  const [state, setState] = useState("");
  const [domain, setDomain] = useState("");
  const load = useCallback(() => api.aiHubTrustedDomains().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!state.trim() || !domain.trim()) return toast.error("State and domain required");
    try { await api.aiHubAddTrustedDomain({ state: state.trim(), domain: domain.trim() }); setDomain(""); toast.success("Domain added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.aiHubDeleteTrustedDomain(id); load(); } catch { toast.error("Failed"); } };
  if (!rows) return <Loader2 className="animate-spin text-[#FF6A00]" />;
  const byState = rows.reduce((m, r) => { (m[r.state] = m[r.state] || []).push(r); return m; }, {});
  return (
    <div className="space-y-5" data-testid="aihub-domains">
      {canEdit && (
        <Panel title="Add trusted domain">
          <div className="flex flex-wrap gap-2 items-end">
            <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State (e.g. Oregon)" className={`${inp} max-w-[200px]`} data-testid="aihub-domain-state" />
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="domain.gov" className={`${inp} max-w-[220px]`} data-testid="aihub-domain-input" />
            <button onClick={add} className="inline-flex items-center gap-1.5 bg-[#1F2A22] text-white px-3.5 py-2 text-sm" data-testid="aihub-domain-add"><Plus size={15} /> Add</button>
          </div>
        </Panel>
      )}
      <Panel title={`Trusted official domains (${rows.length})`}>
        <div className="space-y-4">
          {Object.keys(byState).sort().map((st) => (
            <div key={st}>
              <div className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61] mb-1.5">{st}</div>
              <div className="flex flex-wrap gap-2">
                {byState[st].map((r) => (
                  <span key={r.id} className="inline-flex items-center gap-1.5 bg-[#F2F0EB] px-2.5 py-1 text-[13px] text-[#1F2A22]" data-testid={`aihub-domain-${r.id}`}>
                    {r.domain}
                    {canEdit && <button onClick={() => del(r.id)} className="text-[#B04A3A] hover:opacity-70" data-testid={`aihub-domain-del-${r.id}`}><Trash2 size={12} /></button>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function Discovered({ canEdit }) {
  const [status, setStatus] = useState("new");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.aiHubDiscovered(status).then(setRows).catch(() => setRows([])), [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { await api.aiHubDiscoveredAction(id, { action }); toast.success(action === "add" ? "Added for indexing" : `Marked ${action}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!rows) return <Loader2 className="animate-spin text-[#FF6A00]" />;
  return (
    <div className="space-y-4" data-testid="aihub-discovered">
      <div className="flex gap-2">
        {["new", "added", "ignored", "blocked", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-2.5 py-1 text-xs uppercase tracking-wide ${status === s ? "bg-[#1F2A22] text-white" : "bg-[#F2F0EB] text-[#5C6B61]"}`} data-testid={`aihub-disc-filter-${s}`}>{s}</button>
        ))}
      </div>
      <Panel title={`AI-discovered official sources (${rows.length})`}>
        {rows.length === 0 ? <p className="text-sm text-[#8A8478]">No sources in this state. HomeRange AI logs official sources it finds on the web here for your review.</p> : (
          <div className="divide-y divide-[#E5E2DC]">
            {rows.map((r) => (
              <div key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`aihub-disc-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-sm text-[#1F2A22] font-medium hover:text-[#FF6A00] truncate inline-flex items-center gap-1">{r.title} <ExternalLink size={12} /></a>
                    {r.official && <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 bg-[#2E7D46]/12 text-[#2E7D46]">official</span>}
                  </div>
                  <div className="text-[11px] text-[#8A8478]">{r.state} · {r.domain} · from “{r.from_query}”</div>
                </div>
                {canEdit && r.status === "new" && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => act(r.id, "add")} title="Add to knowledge base" className="p-1.5 text-[#2E7D46] hover:bg-[#2E7D46]/10" data-testid={`aihub-disc-add-${r.id}`}><CheckCircle2 size={16} /></button>
                    <button onClick={() => act(r.id, "ignore")} title="Ignore" className="p-1.5 text-[#8A8478] hover:bg-[#8A8478]/10" data-testid={`aihub-disc-ignore-${r.id}`}><XCircle size={16} /></button>
                    <button onClick={() => act(r.id, "block")} title="Block domain" className="p-1.5 text-[#B04A3A] hover:bg-[#B04A3A]/10" data-testid={`aihub-disc-block-${r.id}`}><Ban size={16} /></button>
                  </div>
                )}
                {r.status !== "new" && <span className="text-[10px] uppercase tracking-wide text-[#8A8478] shrink-0">{r.status}</span>}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Usage() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.aiHubUsage(days).then(setD).catch(() => setD(null)); }, [days]);
  if (!d) return <Loader2 className="animate-spin text-[#FF6A00]" />;
  const maxDay = Math.max(1, ...d.by_day.map((x) => x.count));
  return (
    <div className="space-y-5" data-testid="aihub-usage">
      <div className="flex gap-2">
        {[7, 30, 90].map((n) => <button key={n} onClick={() => setDays(n)} className={`px-2.5 py-1 text-xs ${days === n ? "bg-[#1F2A22] text-white" : "bg-[#F2F0EB] text-[#5C6B61]"}`} data-testid={`aihub-usage-${n}`}>{n}d</button>)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Requests" value={d.total_requests} />
        <KpiCard label="Web-grounded" value={d.web_requests} />
        <KpiCard label="Avg latency" value={`${d.avg_latency_ms}ms`} />
        <KpiCard label="Errors" value={`${d.error_pct}%`} />
        <KpiCard label="Est. cost" value={`$${d.est_cost_usd}`} />
      </div>
      <Panel title="Requests per day">
        {d.by_day.length === 0 ? <p className="text-sm text-[#8A8478]">No requests yet in this window.</p> : (
          <div className="flex items-end gap-1.5 h-32">
            {d.by_day.map((x) => (
              <div key={x.day} className="flex-1 flex flex-col items-center justify-end" title={`${x.day}: ${x.count}`}>
                <div className="w-full bg-[#2E7D46]" style={{ height: `${(x.count / maxDay) * 100}%` }} />
                <span className="text-[9px] text-[#8A8478] mt-1 rotate-0">{x.day.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-[#8A8478] mt-3">Fallback (Claude) requests: {d.fallback_requests} · Cost is an indicative estimate, not a bill.</p>
      </Panel>
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.aiHubAudit().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <Loader2 className="animate-spin text-[#FF6A00]" />;
  return (
    <Panel title={`Recent admin activity (${rows.length})`} data-testid="aihub-audit">
      {rows.length === 0 ? <p className="text-sm text-[#8A8478]">No AI admin actions logged yet.</p> : (
        <div className="divide-y divide-[#E5E2DC]">
          {rows.map((r) => (
            <div key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="text-[#1F2A22]"><span className="font-medium">{r.action.replace(/_/g, " ")}</span> <span className="text-[#8A8478]">{JSON.stringify(r.detail)}</span></span>
              <span className="text-[11px] text-[#8A8478] shrink-0">{r.actor} · {new Date(r.ts).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export default function AdminAI() {
  const { can } = useAdmin();
  const canEdit = can ? can("states") : true;
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-6">
      <div>
        <p className={OVERLINE}>HomeRange AI</p>
        <h1 className={H1}>AI Command Center</h1>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-[#E5E2DC]">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm -mb-px border-b-2 transition-colors ${tab === t.id ? "border-[#FF6A00] text-[#1F2A22] font-medium" : "border-transparent text-[#8A8478] hover:text-[#1F2A22]"}`} data-testid={`aihub-tab-${t.id}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "domains" && <TrustedDomains canEdit={canEdit} />}
      {tab === "discovered" && <Discovered canEdit={canEdit} />}
      {tab === "usage" && <Usage />}
      {tab === "audit" && <Audit />}
    </div>
  );
}
